import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { Link, useLocation } from "react-router";
import { useSettingsBarStyles } from "./SettingsBar.styles";
import { useTranslation } from "../../../localization/useTranslation";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useFeatureFlags, usePlanContext } from "../../../auth/planProvider/PlanProvider";
import { useUrlProvider } from "../../../useUrlProvider";
import { DotLegalPrettyScrollbar, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import FullDataExportDialog from "../../fullDataExportDialog/FullDataExportDialog";
import TransferContractAccessBusinessAreaDialog from "../../../administrativeFunctions/transferContractAccessBusinessAreaDialog/TransferContractAccessBusinessAreaDialog";
import GlobalSettingsDialog from "../../../riskSettings/globalSettings/GlobalSettingsDialog";
import ContractGlobalSettingsDialog from "../../../contract/contractGlobalSettings/ContractGlobalSettingsDialog";
import NavigationMenuSettingsDialog from "../../../navigationMenuSettings/NavigationMenuSettingsDialog";
import { PurpleLockIcon } from "@common/icons";
import PlanDialogButtonWrapper from "../../../plan/planDialogWrapper/planDialogWrapper";
import { TemplateEntityType } from "../../../entityTemplates/EntityTemplate.types";
import { FeatureFlag } from "../../../auth/planProvider/PlanProvider.types.ts";

interface SettingsMenuItem {
    labelKey: string;
    path?: string;
    additionalActivePaths?: string[];
    visible: boolean;
    onClick?: () => void;
    tooltip?: string;
    locked?: boolean;
    lockedTooltip?: string;
    active?: boolean;
}

interface SettingsMenuSection {
    titleKey: string;
    items: SettingsMenuItem[];
}

function SettingsBar() {
    const styles = useSettingsBarStyles();
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { addonsFeatures, vendorManagementFeatures, platformFeatures, contractManagementFeatures } = usePlanContext();
    const urlProvider = useUrlProvider();
    const location = useLocation();
    const [showFullDataExportDialog, setShowFullDataExportDialog] = useState(false);
    const [showTransferContractAccessDialog, setShowTransferContractAccessDialog] = useState(false);
    const [showGlobalSettingsDialog, setShowGlobalSettingsDialog] = useState(false);
    const [showContractGlobalSettingsDialog, setShowContractGlobalSettingsDialog] = useState(false);
    const [showNavigationMenuSettingsDialog, setShowNavigationMenuSettingsDialog] = useState(false);
    const featureFlag = useFeatureFlags();

    const canManageRiskSettings = permissions.canManageMasterData && permissions.canAccessEnterpriseRiskManagement;
    const enterpriseRiskManagementLocked = !addonsFeatures.enterpriseRiskManagement;

    const sections: SettingsMenuSection[] = [
        {
            titleKey: "accountManagement",
            items: [
                {
                    labelKey: "users",
                    path: urlProvider.getUsersUrl(),
                    additionalActivePaths: ["/privacy/user/"],
                    visible: permissions.canManageUsers,
                    tooltip: translateString("manageUsersBodyText"),
                },
                {
                    labelKey: "externalUsers",
                    path: urlProvider.getExternalUsersUrl(),
                    visible: permissions.canManageUsers,
                    tooltip: translateString("manageExternalUsersDescription"),
                },
                { labelKey: "roles", path: urlProvider.getUserRolesUrl(), visible: permissions.canManageRoles, tooltip: translateString("manageRolesInfo") },
                {
                    labelKey: "emailConfiguration",
                    path: urlProvider.getEmailCustomizationUrl(),
                    visible: permissions.canManageMasterData,
                    tooltip: translateString("emailCustomizationDescription"),
                },
                {
                    labelKey: "navigationMenuSettings",
                    onClick: () => setShowNavigationMenuSettingsDialog(true),
                    visible: permissions.canManageMasterData,
                    tooltip: translateString("navigationMenuSettingsDescription"),
                    active: showNavigationMenuSettingsDialog,
                },
                {
                    labelKey: "aiAdministration",
                    path: urlProvider.getAiAdministrationUrl(),
                    visible: permissions.canManageAiAdministration,
                    tooltip: translateString("aiAdministrationDescription"),
                },
                {
                    labelKey: "apiKeys",
                    path: urlProvider.getApiKeysUrl(),
                    visible: permissions.canManageApiKeys,
                    locked: !addonsFeatures.publicApi,
                    tooltip: translateString("apiKeysDescription"),
                },
            ],
        },
        {
            titleKey: "dataManagement",
            items: [
                {
                    labelKey: "masterData",
                    path: urlProvider.getMasterDataUrl(),
                    visible: permissions.canManageMasterData,
                    tooltip: translateString("manageMasterDataBody"),
                },
                {
                    labelKey: "fullDataExportHeader",
                    visible: permissions.canDoFullDataExport,
                    onClick: () => setShowFullDataExportDialog(true),
                    tooltip: translateString("fullDataExportDescription"),
                    active: showFullDataExportDialog,
                },
                {
                    labelKey: "businessAreaAccessContracts",
                    visible: permissions.canManageMasterData && permissions.contractManagementPermissions.permissions.edit,
                    onClick: () => setShowTransferContractAccessDialog(true),
                    locked: !platformFeatures.canTransferBusinessAreaAccess,
                    lockedTooltip: translateString("upgradeForAccessToTransferBusinessAreaAccess"),
                    active: showTransferContractAccessDialog,
                },
                {
                    labelKey: "contractSettings",
                    visible:
                        permissions.canManageMasterData &&
                        permissions.contractManagementPermissions.permissions.edit &&
                        contractManagementFeatures.hasContractManagement,
                    onClick: () => setShowContractGlobalSettingsDialog(true),
                    active: showContractGlobalSettingsDialog,
                },
            ],
        },
        {
            titleKey: "riskSettings",
            items: [
                {
                    labelKey: "riskAreas",
                    path: urlProvider.getRiskAreasUrl(),
                    visible: canManageRiskSettings,
                    locked: enterpriseRiskManagementLocked,
                    lockedTooltip: translateString("upgradeToEnterpriseRiskManagement"),
                    tooltip: translateString("riskAreas"),
                },
                {
                    labelKey: "riskScenarios",
                    path: urlProvider.getRiskScenariosUrl(),
                    visible: canManageRiskSettings,
                    locked: enterpriseRiskManagementLocked,
                    lockedTooltip: translateString("upgradeToEnterpriseRiskManagement"),
                    tooltip: translateString("riskScenarios"),
                },
                {
                    labelKey: "riskConsequences",
                    path: urlProvider.getConsequencesUrl(),
                    visible: canManageRiskSettings,
                    locked: enterpriseRiskManagementLocked,
                    lockedTooltip: translateString("upgradeToEnterpriseRiskManagement"),
                    tooltip: translateString("riskConsequences"),
                },
                {
                    labelKey: "securityMeasures",
                    path: urlProvider.getSecurityMeasures(),
                    visible: canManageRiskSettings,
                    locked: enterpriseRiskManagementLocked,
                    lockedTooltip: translateString("upgradeToEnterpriseRiskManagement"),
                    tooltip: translateString("securityMeasures"),
                },
                {
                    labelKey: "globalRiskSettings",
                    onClick: () => setShowGlobalSettingsDialog(true),
                    visible: canManageRiskSettings,
                    locked: enterpriseRiskManagementLocked,
                    lockedTooltip: translateString("upgradeToEnterpriseRiskManagement"),
                    tooltip: translateString("globalRiskSettings"),
                    active: showGlobalSettingsDialog,
                },
            ],
        },
        {
            titleKey: "templatesAndCustomFields",
            items: [
                {
                    labelKey: "contractInputFields",
                    path: urlProvider.getInputFieldsUrl(),
                    visible: permissions.canManageMasterData,
                    locked: !addonsFeatures.entityTemplates,
                    lockedTooltip: translateString("upgradeToTemplatesAndCustomFields"),
                    tooltip: translateString("administrateContractInputFields"),
                },
                {
                    labelKey: "contractTemplates",
                    path: urlProvider.getEntityTemplates(TemplateEntityType.Contract),
                    visible: permissions.canManageMasterData,
                    locked: !addonsFeatures.entityTemplates,
                    lockedTooltip: translateString("upgradeToTemplatesAndCustomFields"),
                    tooltip: translateString("administrateContractTemplates"),
                },
                {
                    labelKey: "systemInputFields",
                    path: urlProvider.getSystemInputFieldsUrl(),
                    visible: permissions.canManageMasterData,
                    locked: !addonsFeatures.entityTemplates,
                    lockedTooltip: translateString("upgradeToTemplatesAndCustomFields"),
                    tooltip: translateString("administrateSystemInputFields"),
                },
                {
                    labelKey: "systemTemplates",
                    path: urlProvider.getEntityTemplates(TemplateEntityType.System),
                    visible: permissions.canManageMasterData,
                    locked: !addonsFeatures.entityTemplates,
                    lockedTooltip: translateString("upgradeToTemplatesAndCustomFields"),
                    tooltip: translateString("administrateSystemTemplates"),
                },
                {
                    labelKey: "legalEntityInputFields",
                    path: urlProvider.getLegalEntityInputFieldsUrl(),
                    visible: permissions.canManageMasterData,
                    locked: !addonsFeatures.entityTemplates,
                    lockedTooltip: translateString("upgradeToTemplatesAndCustomFields"),
                    tooltip: translateString("administrateLegalEntityInputFields"),
                },
                {
                    labelKey: "legalEntityTemplates",
                    path: urlProvider.getEntityTemplates(TemplateEntityType.LegalEntity),
                    visible: permissions.canManageMasterData,
                    locked: !addonsFeatures.entityTemplates,
                    lockedTooltip: translateString("upgradeToTemplatesAndCustomFields"),
                    tooltip: translateString("administrateLegalEntityTemplates"),
                },
                {
                    labelKey: "incidentLogInputFields",
                    path: urlProvider.getIncidentLogInputFieldsUrl(),
                    visible: featureFlag.isEnabled(FeatureFlag.IncidentLogTemplates) && permissions.canManageMasterData,
                    locked: !addonsFeatures.entityTemplates,
                    lockedTooltip: translateString("upgradeToTemplatesAndCustomFields"),
                    tooltip: translateString("administrateIncidentLogInputFields"),
                },
                {
                    labelKey: "incidentLogTemplates",
                    path: urlProvider.getEntityTemplates(TemplateEntityType.IncidentLog),
                    visible: featureFlag.isEnabled(FeatureFlag.IncidentLogTemplates) && permissions.canManageMasterData,
                    locked: !addonsFeatures.entityTemplates,
                    lockedTooltip: translateString("upgradeToTemplatesAndCustomFields"),
                    tooltip: translateString("administrateIncidentLogTemplates"),
                },
                {
                    labelKey: "processInputFields",
                    path: urlProvider.getProcessInputFieldsUrl(),
                    visible: featureFlag.isEnabled(FeatureFlag.ProcessTemplates) && permissions.canManageMasterData,
                    locked: !addonsFeatures.entityTemplates,
                    lockedTooltip: translateString("upgradeToTemplatesAndCustomFields"),
                    tooltip: translateString("administrateProcessInputFields"),
                },
                {
                    labelKey: "processTemplates",
                    path: urlProvider.getEntityTemplates(TemplateEntityType.Process),
                    visible: featureFlag.isEnabled(FeatureFlag.ProcessTemplates) && permissions.canManageMasterData,
                    locked: !addonsFeatures.entityTemplates,
                    lockedTooltip: translateString("upgradeToTemplatesAndCustomFields"),
                    tooltip: translateString("administrateProcessTemplates"),
                },
                {
                    labelKey: "audits",
                    path: urlProvider.getAuditsUrl(),
                    visible: vendorManagementFeatures.hasVendorManagement && permissions.canManageMasterData,
                    tooltip: translateString("administrateAudits"),
                },
                {
                    labelKey: "vendorClassifications",
                    path: urlProvider.getVendorClassifications(),
                    visible: vendorManagementFeatures.hasVendorManagement && permissions.canManageMasterData,
                    tooltip: translateString("administrateVendors"),
                },
            ],
        },
        {
            titleKey: "dotLegalAdministration",
            items: [
                {
                    labelKey: "customers",
                    path: urlProvider.getCustomersUrl(),
                    visible: permissions.canManageCustomers,
                    tooltip: translateString("manageCustomerInfo"),
                },
                {
                    labelKey: "systemAnnouncements",
                    path: urlProvider.getSystemNotificationsUrl(),
                    visible: permissions.canManageCustomers,
                    tooltip: translateString("manageSystemAnnouncementsDescription"),
                },
                {
                    labelKey: "frameworkTemplates",
                    path: urlProvider.getFrameworkTemplatesUrl(),
                    visible: permissions.canManageCustomers && permissions.canManageStandardTemplateData,
                    tooltip: translateString("administrateFrameworkTemplates"),
                },
                {
                    labelKey: "annualWheelActivityStandardTemplates",
                    path: urlProvider.getAnnualWheelActivityStandardTemplatesUrl(),
                    visible: permissions.canManageCustomers && permissions.canManageStandardTemplateData,
                    tooltip: translateString("administrateAnnualWheelTemplates"),
                },
            ],
        },
    ];

    const anyDialogOpen =
        showFullDataExportDialog ||
        showTransferContractAccessDialog ||
        showContractGlobalSettingsDialog ||
        showGlobalSettingsDialog ||
        showNavigationMenuSettingsDialog;

    const isActive = (item: SettingsMenuItem) => {
        if (anyDialogOpen) return false;
        const paths = [item.path, ...(item.additionalActivePaths ?? [])].filter(Boolean) as string[];
        return paths.some((path) => location.pathname.startsWith(path));
    };

    const visibleSections = sections.filter((section) => section.items.some((item) => item.visible));

    return (
        <>
            <Box sx={styles.container}>
                {/* Absolute so the menu items do not add to the page height; the bar keeps stretching to the layout's height and scrolls its items instead */}
                <DotLegalPrettyScrollbar sx={{ position: "absolute", inset: 0 }}>
                    <Box sx={{ paddingTop: 2, paddingBottom: 3 }}>
                        <Typography sx={styles.header}>{translateString("settings")}</Typography>

                        {visibleSections.map((section) => (
                            <Box key={section.titleKey} sx={styles.section}>
                                <Typography sx={styles.sectionHeader}>{translateString(section.titleKey)}</Typography>
                                {section.items
                                    .filter((item) => item.visible)
                                    .map((item) => {
                                        const label = (
                                            <>
                                                {translateString(item.labelKey)}
                                                {item.locked && (
                                                    <Box component="span" sx={{ "& svg": { height: 14, width: 14, marginLeft: 0.5, marginBottom: "-2px" } }}>
                                                        {PurpleLockIcon}
                                                    </Box>
                                                )}
                                            </>
                                        );

                                        const menuItemContent =
                                            item.path && !item.locked ? (
                                                <Box
                                                    key={item.labelKey}
                                                    component={Link}
                                                    to={item.path}
                                                    sx={[styles.menuItem, isActive(item) ? styles.activeMenuItem : {}]}
                                                >
                                                    <Box sx={{ marginLeft: "-3px" }}>{label}</Box>
                                                </Box>
                                            ) : (
                                                <Box
                                                    key={item.labelKey}
                                                    sx={[styles.menuItem, item.active ? styles.activeMenuItem : {}]}
                                                    onClick={item.locked ? undefined : item.onClick}
                                                >
                                                    <Box sx={{ marginLeft: "-3px" }}>{label}</Box>
                                                </Box>
                                            );

                                        let wrappedContent = menuItemContent;

                                        if (item.locked) {
                                            wrappedContent = (
                                                <PlanDialogButtonWrapper
                                                    key={item.labelKey}
                                                    enabled
                                                    text={item.lockedTooltip ?? translateString("upgradeToActivateAddOn")}
                                                    hoverText={item.lockedTooltip}
                                                    position="right-end"
                                                >
                                                    {menuItemContent}
                                                </PlanDialogButtonWrapper>
                                            );
                                        }

                                        if (item.tooltip && !item.locked) {
                                            return (
                                                <DotLegalTooltip key={item.labelKey} text={item.tooltip} position="right">
                                                    {wrappedContent}
                                                </DotLegalTooltip>
                                            );
                                        }

                                        return wrappedContent;
                                    })}
                            </Box>
                        ))}
                    </Box>
                </DotLegalPrettyScrollbar>
            </Box>

            {showFullDataExportDialog && <FullDataExportDialog onDialogClose={() => setShowFullDataExportDialog(false)} />}
            {showTransferContractAccessDialog && <TransferContractAccessBusinessAreaDialog onDialogClose={() => setShowTransferContractAccessDialog(false)} />}
            {showContractGlobalSettingsDialog && <ContractGlobalSettingsDialog onDialogClose={() => setShowContractGlobalSettingsDialog(false)} />}
            {showGlobalSettingsDialog && <GlobalSettingsDialog onDialogClose={() => setShowGlobalSettingsDialog(false)} />}
            {showNavigationMenuSettingsDialog && <NavigationMenuSettingsDialog onDialogClose={() => setShowNavigationMenuSettingsDialog(false)} />}
        </>
    );
}

export default SettingsBar;
